// /Users/joshua.moore1153/Desktop/Sinclair Apps/spcore-v4/src/components/CcpAdminAnnouncements/inc/EditForm.js
import React, { useState, useEffect } from "react"; // Import useEffect
import {
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert, // Import Alert for potential errors
} from "@mui/material";
import { Save } from "@mui/icons-material";
import { Timestamp } from "firebase/firestore"; // Import Timestamp

// Helper function to convert MM/DD/YYYY string or Firestore Timestamp to YYYY-MM-DD
const formatToInputDate = (dateValue) => {
  if (!dateValue) return "";

  // Handle Firestore Timestamp objects
  if (typeof dateValue === "object" && dateValue.seconds) {
    const date = new Date(dateValue.seconds * 1000);
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }

  // Handle string format
  if (typeof dateValue === "string") {
    const parts = dateValue.split("/"); // [MM, DD, YYYY]
    if (parts.length === 3) {
      // Ensure month and day have leading zeros if needed
      const month = parts[0].padStart(2, "0");
      const day = parts[1].padStart(2, "0");
      const year = parts[2];
      return `${year}-${month}-${day}`;
    }
  }

  return ""; // Return empty if format is unexpected
};

// Helper function to convert YYYY-MM-DD string to Firestore Timestamp
const convertInputDateToTimestamp = (dateString) => {
  if (!dateString) return null; // Or handle as needed
  try {
    const dateParts = dateString.split("-"); // [YYYY, MM, DD]
    const utcDate = new Date(
      Date.UTC(
        parseInt(dateParts[0], 10),
        parseInt(dateParts[1], 10) - 1, // Month is 0-indexed
        parseInt(dateParts[2], 10)
      )
    );
    if (!isNaN(utcDate.getTime())) {
      return Timestamp.fromDate(utcDate);
    } else {
      throw new Error("Invalid date format.");
    }
  } catch (e) {
    console.error("Error converting date string to Timestamp:", e);
    return null; // Indicate conversion failure
  }
};

const EditForm = ({ initialData, onUpdate }) => {
  const [title, setTitle] = useState(initialData.title || "");
  const [color, setColor] = useState(initialData.color || "");
  // Store the date in the format needed by the input (YYYY-MM-DD)
  const [expireDateInput, setExpireDateInput] = useState("");
  const [content, setContent] = useState(initialData.content || "");
  const [url, setUrl] = useState(initialData.url || ""); // Add URL state
  const [linkText, setLinkText] = useState(initialData.linkText || ""); // Add link text state
  const [formError, setFormError] = useState(null); // For date conversion errors
  const [isSubmitting, setIsSubmitting] = useState(false); // Prevent double submit

  // Effect to format the initial date when the component mounts or initialData changes
  useEffect(() => {
    setExpireDateInput(formatToInputDate(initialData.expireDate));
    // Reset other fields if initialData changes (optional, depends on desired behavior)
    setTitle(initialData.title || "");
    setColor(initialData.color || "");
    setContent(initialData.content || "");
    setUrl(initialData.url || ""); // Reset URL field
    setLinkText(initialData.linkText || ""); // Reset link text field
    setFormError(null); // Clear any previous errors
    setIsSubmitting(false); // Reset submitting state
  }, [initialData]); // Rerun when initialData changes

  const handleFormSubmit = (event) => {
    event.preventDefault();
    setFormError(null); // Clear previous errors
    setIsSubmitting(true);

    // --- URL Validation ---
    if (url && url.trim()) {
      try {
        new URL(url.trim());
      } catch (urlError) {
        setFormError("Please enter a valid URL (e.g., https://example.com)");
        setIsSubmitting(false);
        return;
      }
    }

    // Convert the input date string back to a Timestamp
    const expireTimestamp = convertInputDateToTimestamp(expireDateInput);

    if (expireDateInput && !expireTimestamp) {
      // Handle case where date input is filled but conversion failed
      setFormError("Invalid Expire Date format. Please use YYYY-MM-DD.");
      setIsSubmitting(false);
      return; // Stop submission
    }
    if (!expireDateInput) {
      // Handle case where date is required but empty
      setFormError("Expire Date is required.");
      setIsSubmitting(false);
      return; // Stop submission
    }

    const updatedData = {
      title: title,
      color: color,
      expireDate: expireTimestamp, // Use the converted Timestamp
      content: content,
      url: url.trim(), // Add URL to updated data (trimmed)
      linkText: linkText.trim(), // Add link text to updated data (trimmed)
    };

    // Call the parent's update function
    // Wrap in a try/catch if onUpdate itself might throw and you want to handle it here
    try {
      onUpdate(updatedData);
      // Note: Success feedback/dialog closing is handled in the parent (List.js)
    } catch (error) {
      // This catch block might not be strictly necessary if List.js handles errors from updateData
      console.error("Error during update submission:", error);
      setFormError(`Update failed: ${error.message || "Please try again."}`);
      setIsSubmitting(false); // Re-enable button on error
    }
    // No finally block needed here as parent handles closing/state reset
  };

  return (
    <form onSubmit={handleFormSubmit}>
      {/* Optional: Display form error inside the dialog */}
      {formError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {formError}
        </Alert>
      )}
      <TextField
        label="Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        fullWidth
        required
        margin="normal"
        disabled={isSubmitting}
      />
      <FormControl fullWidth required margin="normal" disabled={isSubmitting}>
        <InputLabel id="edit-color-select-label">Color</InputLabel>
        <Select
          labelId="edit-color-select-label"
          value={color}
          onChange={(e) => setColor(e.target.value)}
          label="Color"
        >
          <MenuItem value="info">Blue</MenuItem>
          <MenuItem value="success">Green</MenuItem>
          <MenuItem value="warning">Orange</MenuItem>
          <MenuItem value="error">Red</MenuItem>
        </Select>
      </FormControl>
      <TextField
        label="Expire Date"
        value={expireDateInput} // Bind to the state variable holding YYYY-MM-DD
        onChange={(e) => setExpireDateInput(e.target.value)}
        fullWidth
        required
        margin="normal"
        type="date" // Input type expects YYYY-MM-DD
        InputLabelProps={{
          shrink: true,
        }}
        disabled={isSubmitting}
      />
      <TextField
        label="Content"
        value={content}
        onChange={(e) => setContent(e.target.value)}
        fullWidth
        required
        margin="normal"
        multiline
        rows={4}
        disabled={isSubmitting}
      />
      <TextField
        label="URL (optional)"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        fullWidth
        margin="normal"
        placeholder="https://example.com"
        disabled={isSubmitting}
        helperText="Optional: Add a link that users can click to learn more"
      />
      <TextField
        label="Link Text (optional)"
        value={linkText}
        onChange={(e) => setLinkText(e.target.value)}
        fullWidth
        margin="normal"
        placeholder="Learn More"
        disabled={isSubmitting}
        helperText="Custom text for the link button (only used if URL is provided)"
      />
      <Button
        type="submit"
        variant="contained"
        color="primary"
        endIcon={<Save />}
        sx={{ mt: 2 }}
        disabled={isSubmitting} // Disable while submitting
      >
        {isSubmitting ? "Updating..." : "Update Announcement"}
      </Button>
    </form>
  );
};

export default EditForm;
